import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { getAllImages, getProductImage } from '../../utils/imageUtils';
import ImageLightbox from './ImageLightbox';

/**
 * ProductCatalogueModal - Modal showing product image gallery with zoom
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.product - Product data
 */
const ProductCatalogueModal = ({ isOpen, onClose, product }) => {
  const navigate = useNavigate();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  const images = product ? getAllImages(product.images) : [];
  const hasImages = images.length > 0;

  // Reset selected image when product changes
  useEffect(() => {
    setSelectedImageIndex(0);
    setShowLightbox(false);
  }, [product?.id]);

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !showLightbox) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, showLightbox, onClose]);

  if (!isOpen || !product) {
    return null;
  }

  const handleViewProductDetails = () => {
    onClose();
    navigate(`/products/${product.id}`);
  };

  const handlePrevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex < images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 truncate pr-4">
              {product.name}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image Gallery Section */}
              <div className="space-y-4">
                {/* Main Image */}
                <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden group">
                  {hasImages ? (
                    <>
                      <img
                        src={images[selectedImageIndex]}
                        alt={`${product.name} - Image ${selectedImageIndex + 1}`}
                        className="w-full h-full object-cover cursor-zoom-in transition-transform duration-300"
                        onClick={() => setShowLightbox(true)}
                      />
                      {/* Navigation Arrows */}
                      {images.length > 1 && (
                        <>
                          {selectedImageIndex > 0 && (
                            <button
                              onClick={handlePrevImage}
                              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Previous image"
                            >
                              <ChevronLeft className="w-5 h-5 text-gray-700" />
                            </button>
                          )}
                          {selectedImageIndex < images.length - 1 && (
                            <button
                              onClick={handleNextImage}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Next image"
                            >
                              <ChevronRight className="w-5 h-5 text-gray-700" />
                            </button>
                          )}
                        </>
                      )}
                      {/* Click to zoom hint */}
                      <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                        Click to zoom
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-24 h-24 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Thumbnails */}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          idx === selectedImageIndex
                            ? 'border-accent-gold ring-2 ring-accent-gold/30'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Info Section */}
              <div className="space-y-4">
                {/* Product Code */}
                <div>
                  <span className="text-xs font-mono font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black border border-accent-goldDark shadow-sm">
                    {product.product_code}
                  </span>
                </div>

                {/* Product Name */}
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                  {product.name}
                </h3>

                {/* Key Details */}
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Brand</span>
                    <span className="font-semibold text-gray-900">{product.brand_name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Size</span>
                    <span className="font-semibold text-gray-900">{product.size_dimension || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Tile Type</span>
                    <span className="font-semibold text-gray-900">{product.tile_type_name || 'N/A'}</span>
                  </div>
                  {product.use_case_name && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Use Case</span>
                      <span className="font-semibold text-gray-900">{product.use_case_name}</span>
                    </div>
                  )}
                </div>

                {/* Image Count Info */}
                {hasImages && (
                  <p className="text-sm text-gray-500">
                    {images.length} image{images.length !== 1 ? 's' : ''} available
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleViewProductDetails}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-accent-gold hover:bg-accent-goldDark text-primary-black font-semibold rounded-lg transition-colors min-h-[48px]"
            >
              <ExternalLink className="w-5 h-5" />
              View Product Details
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox for zoomed image */}
      <ImageLightbox
        isOpen={showLightbox}
        onClose={() => setShowLightbox(false)}
        images={images}
        currentIndex={selectedImageIndex}
        onNavigate={setSelectedImageIndex}
      />
    </>
  );
};

export default ProductCatalogueModal;
